import { useMemo, useState } from 'react'
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded'
import {
  IconButton,
  LeaveWorkspaceConfirmModal,
  WorkspaceSwitcherDropdown,
  WorkspaceSwitcherRow,
} from '../Common'
import { leaveWorkspaceAndPurgeOutbox } from '../../services/memberService'
import { requestHelperWorkspaceSwitch, type HelperWorkspaceSummary } from '../../services/helper/helperStore'
import { resolveSwitchLanding } from '../../services/workspaceSwitch/switchLanding'
import { helperViewFromPathname } from './helperShellRouting'
import type { WorkspaceRole } from '../../types/workspace'
import type { HelperMutationRecord } from '../../services/helper/helperOutboxDb'
import { pendingCountFor } from '../../services/helper/helperWorkspaceService'
import { useNls } from '../../nls/nlsProvider'

const formatRole = (role: string | null | undefined): string => {
  if (!role) return ''
  const lower = role.toLowerCase()
  if (lower === 'readonly') return 'Read-only'
  return lower.charAt(0).toUpperCase() + lower.slice(1)
}

export type WorkspaceSwitcherProps = {
  workspaces: HelperWorkspaceSummary[]
  activeWorkspaceId: string | null
  pending: HelperMutationRecord[]
  onOpenIncomingInvites?: () => void
}

export const WorkspaceSwitcher = ({
  workspaces,
  activeWorkspaceId,
  pending,
}: WorkspaceSwitcherProps) => {
  const { t } = useNls()
  const [leavingWorkspace, setLeavingWorkspace] = useState<HelperWorkspaceSummary | null>(null)
  const active = useMemo(
    () => workspaces.find((entry) => entry.id === activeWorkspaceId) ?? null,
    [workspaces, activeWorkspaceId],
  )

  const handleSelect = (id: string, close: () => void) => {
    const targetWs = workspaces.find((w) => w.id === id)
    const targetRole: WorkspaceRole = (targetWs?.role as WorkspaceRole) ?? 'HELPER'
    const currentTab = typeof window !== 'undefined' ? helperViewFromPathname(window.location.pathname) : 'TASKS'
    const landing = targetWs
      ? resolveSwitchLanding({
          app: 'HELPER',
          current: currentTab === 'PROFILE' ? 'TASKS' : currentTab,
          target: {
            id: targetWs.id,
            slug: '',
            isPersonal: false,
            kind: 'TEAM',
          },
          role: targetRole,
        })
      : '/helper/dashboard'

    void requestHelperWorkspaceSwitch(id, 'USER', landing).then((switched) => {
      if (switched) close()
    })
  }

  const handleLeftWorkspace = async () => {
    if (!leavingWorkspace) return
    const leftId = leavingWorkspace.id
    setLeavingWorkspace(null)
    const remaining = workspaces.filter((entry) => entry.id !== leftId)
    if (remaining.length > 0) {
      await requestHelperWorkspaceSwitch(remaining[0].id, 'MEMBERSHIP_ENDED', '/helper/dashboard')
    } else {
      await requestHelperWorkspaceSwitch('', 'MEMBERSHIP_ENDED')
    }
  }

  return (
    <>
      <WorkspaceSwitcherDropdown
        testId="helper-workspace-switcher"
        triggerTestId="helper-workspace-trigger"
        listTestId="helper-workspace-list"
        triggerLabel={active ? active.name : t('NLSHelperWorkspaceSelect')}
        triggerLogoUrl={active?.branding?.logoUrl}
        triggerAccentColor={active?.branding?.accentColor || active?.color}
        triggerMaxWidth="max-w-[220px]"
        dropdownWidth="w-[300px]"
        itemCount={workspaces.length}
      >
        {({ search, close }) => {
          const query = search.trim().toLowerCase()
          const visible = query
            ? workspaces.filter((ws) => ws.name.toLowerCase().includes(query))
            : workspaces

          if (visible.length === 0 && workspaces.length > 0) {
            return <p className="text-center text-xs text-[var(--muted)] py-6">{t('NLSHelperWorkspaceNoMatch')}</p>
          }
          if (workspaces.length === 0) {
            return <p className="text-[11px] text-[var(--muted)] px-3 py-2 text-center">{t('NLSHelperWorkspaceEmpty')}</p>
          }

          return visible.map((workspace) => {
            const isActive = workspace.id === activeWorkspaceId
            const isBlocked = workspace.appAccess === false
            const role = formatRole(workspace.role)
            const pendingCount = pendingCountFor(workspace.id, pending)
            const subtitle = [
              role,
              isBlocked ? t('NLSHelperWorkspaceBlockedTag') : '',
              pendingCount > 0 ? t('NLSHelperWorkspacePendingCount', { count: pendingCount }) : '',
            ].filter(Boolean).join(' · ')
            return (
              <WorkspaceSwitcherRow
                key={workspace.id}
                id={workspace.id}
                name={workspace.name}
                isActive={isActive}
                logoUrl={workspace.branding?.logoUrl}
                accentColor={workspace.branding?.accentColor || workspace.color}
                subtitle={subtitle}
                badge={
                  isBlocked ? (
                    <span
                      data-testid={`workspace-blocked-badge-${workspace.id}`}
                      className="text-[10px] px-1.5 py-0.5 rounded-full bg-[var(--surface-muted)] text-[var(--muted)] font-medium shrink-0"
                    >
                      {t('NLSHelperWorkspaceBlockedTag')}
                    </span>
                  ) : undefined
                }
                action={
                  <IconButton
                    variant="tertiary"
                    tone="danger"
                    size="sm"
                    className="mr-1 shrink-0"
                    title={t('NLSInvitesLeaveWorkspaceAction')}
                    label={`${t('NLSInvitesLeaveWorkspaceAction')} - ${workspace.name}`}
                    data-testid={`leave-workspace-${workspace.id}`}
                    icon={<LogoutRoundedIcon fontSize="inherit" />}
                    onClick={(event) => {
                      event.stopPropagation()
                      setLeavingWorkspace(workspace)
                    }}
                  />
                }
                onClick={() => handleSelect(workspace.id, close)}
              />
            )
          })
        }}
      </WorkspaceSwitcherDropdown>

      {leavingWorkspace ? (
        <LeaveWorkspaceConfirmModal
          open={Boolean(leavingWorkspace)}
          workspaceId={leavingWorkspace.id}
          workspaceName={leavingWorkspace.name}
          onClose={() => setLeavingWorkspace(null)}
          onConfirm={async () => {
            await leaveWorkspaceAndPurgeOutbox({ workspaceId: leavingWorkspace.id })
          }}
          onLeft={handleLeftWorkspace}
          onNavigateToOwners={() => setLeavingWorkspace(null)}
        />
      ) : null}
    </>
  )
}
